import { slideUp, slideDown } from "../helpers/Animations.js";
import windowSize from "../services/WindowSize.js";

// Constants
const MOBILE_NAV_BREAKPOINT = 1200;

/**
 * Header component: handles sticky behavior, transparency, and mobile navigation.
 *
 * @package AGDS
 * @category Modules
 * @module Header
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class Header {
    /**
     * Constructor
     *
     * @param {Object} options
     * @param {HTMLElement} options.header
     * @param {HTMLElement} options.overlay
     */
    constructor(options = {}) {
        this.prevScroll = window.scrollY || document.documentElement.scrollTop;
        this.html = document.documentElement;
        this.body = document.body;
        this.header = options.header;
        this.overlay = options.overlay;
		this.scroll = options.scroll;
    }

    /**
     * Initializes.
     */
    init() {
        if (!this.header) {
            console.warn("Header element not found");
            return;
        }

        // Header height
        this.unsubWinSize = windowSize.onChange(
            () => {
                this.headerHeightDetermination();
            },
            { immediate: true },
        );

        // Header scroll
        this.headerScrollInit();

        // Mobile navigation
        this.unsubWinSizeMob = windowSize.onBelow(
            MOBILE_NAV_BREAKPOINT,
            () => {
                this.burgerNav();
                this.navSubMenu();
            },
            { immediate: true },
        );
    }

    /**
     * Header height determination
     */
    headerHeightDetermination() {
        const headerHeight = this.header.clientHeight;

        if (headerHeight > 0) {
            this.html.setAttribute("style", `--agds-header-height: ${headerHeight}px`);
        }

        return headerHeight;
    }

    /**
     * Initializes header behavior while the page scrolls.
     */
    headerScrollInit() {
        this.toggleHeaderTransparency(document.scrollingElement.scrollTop);
        window.addEventListener("scroll", () => this.onScroll());
    }

    /**
     * Handles page scroll events.
     */
    onScroll() {
        const curScroll = window.scrollY || document.documentElement.scrollTop;
        const direction = curScroll > this.prevScroll ? 2 : curScroll < this.prevScroll ? 1 : 0;

        this.toggleHeaderVisibility(curScroll, direction);
        this.toggleHeaderTransparency(curScroll);
        this.prevScroll = curScroll;
    }

    /**
     * Toggles header visibility based on scroll direction.
     *
     * @param {number} curScroll - Current scroll position.
     * @param {number} direction - Scroll direction: 1 up, 2 down, 0 unchanged.
     * @private
     * @returns {void}
     */
    toggleHeaderVisibility(curScroll, direction) {
        const classHide = "header--hide";
        if (direction === 2 && curScroll > 1) {
            this.header.classList.add(classHide);
            this.body.classList.add(classHide);
        } else if (direction === 1) {
            this.header.classList.remove(classHide);
            this.body.classList.remove(classHide);
        }
    }

    /**
     * Toggles header transparency based on scroll position.
     *
     * @param {number} scrollTop - Scroll position from the top of the page.
     * @private
     * @returns {void}
     */
    toggleHeaderTransparency(scrollTop) {
        const classTranp = "header--transparent";
        if (this.body.classList.contains("header-transparent") && scrollTop === 0) {
            this.header.classList.add(classTranp);
            this.body.classList.add(classTranp);
        } else {
            this.header.classList.remove(classTranp);
            this.body.classList.remove(classTranp);
        }
    }

    /**
     * Initializes the burger navigation.
     *
     * @returns {void}
     */
    burgerNav() {
        const header = this.header;
        const burger = header.querySelector(".burger");
        const headerMenu = header.querySelector(".header--nav");
        const links = headerMenu?.querySelectorAll(".menu-item a");

        if (!burger || !headerMenu) {
            return;
        }

        const openNav = () => {
            burger.classList.toggle("_active");
            header.classList.toggle("header-nav--active");
            headerMenu.classList.toggle("_show");
            this.html.classList.toggle("_lock");
        };

        const closeNav = () => {
            burger.classList.remove("_active");
            header.classList.remove("header-nav--active");
            headerMenu.classList.remove("_show");
            this.html.classList.remove("_lock");
        };

        burger.onclick = openNav;

        if (links && links.length) {
            links.forEach((link) => {
                const href = link.getAttribute("href")?.trim();
                if (!href) return;

                const isTriggerOnly = href === "#" || href.startsWith("javascript:");
                if (!isTriggerOnly) {
                    link.onclick = closeNav;
                }
            });
        }
    }

    /**
     * Initializes submenu toggles for menu items with children.
     *
     * @returns {void}
     */
    navSubMenu() {
        const header = this.header;

        // Hide all submenus
        const allSubMenus = header.querySelectorAll(".menu-item-has-children .sub-menu");
        allSubMenus.forEach((menu) => {
            menu.setAttribute("hidden", "");
            menu.classList.remove("_active");
            if (menu.parentElement) menu.parentElement.classList.remove("_active");
        });

        // Remove previous event listener
        if (this._navSubMenuHandler) {
            header.removeEventListener("click", this._navSubMenuHandler);
        }

        // Add new event listener
        const handleMenuClick = (e) => {
            const icon = e.target.closest(".menu-item--icon");
            const link = e.target.closest("a");

            let trigger = null;

            if (icon) {
                trigger = icon;
            } else if (link) {
                const href = link.getAttribute("href");
                if (!href || href === "#" || href.startsWith("javascript")) {
                    trigger = link;
                }
            }

            // Element is not a trigger
            if (!trigger) return;

            // Find parent item
            const parentItem = trigger.closest(".menu-item-has-children");
            if (!parentItem) return;

            // Find submenu
            const targetMenu = parentItem.querySelector(".sub-menu");
            if (!targetMenu) return;

            // Prevent default behavior
            e.preventDefault();
            e.stopPropagation();

            // Toggle submenu
            const isActive = targetMenu.classList.contains("_active");

            if (isActive) {
                slideUp(targetMenu);
                targetMenu.classList.remove("_active");
                parentItem.classList.remove("_active");
            } else {
                slideDown(targetMenu);
                targetMenu.classList.add("_active");
                parentItem.classList.add("_active");
            }
        };

        // Store event handler
        this._navSubMenuHandler = handleMenuClick;
        header.addEventListener("click", handleMenuClick);
    }
}
